import { useMemo, useState } from "react";
import { Badge } from "@/components/ui/badge";
import { Slider } from "@/components/ui/slider";
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, Area, AreaChart } from "recharts";
import { useDisplay } from "@/context/DisplayContext";

const fmt = (v) => `£${Number(v).toLocaleString("en-GB", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const DEBT_COLORS_LIGHT = ["#111827", "#dc2626", "#2563eb", "#16a34a", "#d97706", "#7c3aed", "#0891b2", "#db2777", "#65a30d"];
const DEBT_COLORS_DARK = ["#F9FAFB", "#f87171", "#60a5fa", "#4ade80", "#fbbf24", "#a78bfa", "#22d3ee", "#f472b6", "#a3e635"];
const DEBT_COLORS_HC = ["#FFFFFF", "#FBBF24", "#00FFFF", "#ADFF2F", "#FF69B4", "#FF6347", "#7FFF00", "#FFD700", "#87CEEB"];

function simulatePayoff(debts, strategy, extraMonthly = 0) {
  if (!debts.length) return { months: [], totalInterest: 0, payoffMonth: 0, order: [] };
  const sorted = debts.map(d => ({
    name: d.name, balance: d.total_balance, apr: parseFloat(d.apr_after?.replace("% APR","").replace("%","") || "0"),
    min: d.statement_min,
    minType: d.min_payment_type || "fixed",
    minPct: d.min_payment_pct || 0,
    minFloor: d.min_payment_floor || 5,
  }));
  if (strategy === "avalanche") sorted.sort((a, b) => b.apr - a.apr);
  else sorted.sort((a, b) => a.balance - b.balance);

  const order = sorted.map(d => d.name);
  const balances = sorted.map(d => d.balance);
  const aprs = sorted.map(d => d.apr);
  const totalMin = sorted.reduce((s, d) => s + d.min, 0);
  const monthlyBudget = totalMin + extraMonthly;
  const timeline = [];
  let totalInterest = 0;
  let payoffMonth = 0;

  for (let m = 0; m < 60; m++) {
    const totalBal = balances.reduce((s, b) => s + b, 0);
    if (totalBal <= 0.01) break;
    payoffMonth = m + 1;

    const point = { month: m + 1 };
    let remaining = monthlyBudget;

    // Pay minimums first (dynamic for percentage debts)
    for (let i = 0; i < balances.length; i++) {
      const interest = (balances[i] * (aprs[i] / 100)) / 12;
      totalInterest += interest;
      balances[i] += interest;
      let minPayment;
      if (sorted[i].minType === "percentage" && sorted[i].minPct > 0) {
        minPayment = Math.max(balances[i] * sorted[i].minPct / 100, sorted[i].minFloor);
      } else {
        minPayment = sorted[i].min;
      }
      const payment = Math.min(minPayment, balances[i]);
      balances[i] = Math.max(0, balances[i] - payment);
      remaining -= payment;
    }

    // Extra goes to priority debt
    for (let i = 0; i < balances.length && remaining > 0; i++) {
      if (balances[i] > 0) {
        const extra = Math.min(remaining, balances[i]);
        balances[i] -= extra;
        remaining -= extra;
      }
    }

    sorted.forEach((d, i) => { point[d.name] = Math.round(balances[i] * 100) / 100; });
    point.total = Math.round(balances.reduce((s, b) => s + b, 0) * 100) / 100;
    timeline.push(point);
  }

  return { months: timeline, totalInterest: Math.round(totalInterest * 100) / 100, payoffMonth, order };
}

export default function StrategyPage({ debts }) {
  const [extraPayment, setExtraPayment] = useState(0);
  const [showCustomExtra, setShowCustomExtra] = useState(false);
  const [customExtraVal, setCustomExtraVal] = useState("");
  const { theme } = useDisplay();
  const DEBT_COLORS = theme === "high-contrast" ? DEBT_COLORS_HC : theme === "dark" ? DEBT_COLORS_DARK : DEBT_COLORS_LIGHT;
  const gridColor = theme === "high-contrast" ? "#3F3F46" : theme === "dark" ? "rgba(255,255,255,0.06)" : "#E5E7EB";
  const textColor = theme === "high-contrast" ? "#FFFFFF" : theme === "dark" ? "#9CA3AF" : "#374151";
  const snowball = useMemo(() => simulatePayoff(debts, "snowball", extraPayment), [debts, extraPayment]);
  const avalanche = useMemo(() => simulatePayoff(debts, "avalanche", extraPayment), [debts, extraPayment]);
  const baseSnowball = useMemo(() => simulatePayoff(debts, "snowball", 0), [debts]);
  const baseAvalanche = useMemo(() => simulatePayoff(debts, "avalanche", 0), [debts]);

  const winner = avalanche.totalInterest <= snowball.totalInterest ? "avalanche" : "snowball";
  const savings = Math.abs(avalanche.totalInterest - snowball.totalInterest);
  const isNeutral = savings < 1 || extraPayment === 0;

  return (
    <div data-testid="strategy-page" className="space-y-6">
      <div>
        <h1 className="font-heading text-xl sm:text-2xl md:text-3xl font-semibold tracking-tight">Snowball vs Avalanche</h1>
        <p className="text-xs sm:text-sm text-gray-500 mt-1">Compare debt payoff strategies</p>
      </div>

      {debts.length === 0 ? (
        <div className="border border-gray-200 rounded-sm p-12 text-center">
          <p className="text-gray-400 text-sm">Add debts first to compare strategies.</p>
        </div>
      ) : (
        <>
          {/* What-If Slider */}
          <div className="border border-gray-200 rounded-sm p-5 bg-gray-50" data-testid="what-if-slider">
            <div className="flex items-center justify-between mb-3">
              <div>
                <h3 className="font-heading text-sm font-semibold uppercase tracking-wider text-gray-700">What-If: Extra Monthly Payment</h3>
                <p className="text-xs text-gray-500 mt-0.5">Drag to see how extra payments accelerate your debt payoff</p>
              </div>
              <span className="font-mono-data text-2xl font-bold text-black">{fmt(extraPayment)}<span className="text-sm text-gray-500 font-normal">/mo</span></span>
            </div>
            <Slider
              data-testid="extra-payment-slider"
              value={[extraPayment]}
              onValueChange={(v) => setExtraPayment(v[0])}
              min={0}
              max={500}
              step={25}
              className="mt-2"
            />
            <div className="flex justify-between text-xs text-gray-400 font-mono-data mt-1">
              <span>£0</span>
              <span>£125</span>
              <span>£250</span>
              <span>£375</span>
              <span>£500</span>
            </div>
            <div className="flex gap-2 mt-3 flex-wrap">
              {[0, 50, 100, 200, 500].map((amt) => (
                <button key={amt} data-testid={`quick-set-${amt}`}
                  onClick={() => { setExtraPayment(amt); setShowCustomExtra(false); setCustomExtraVal(""); }}
                  className={`px-3 py-1 text-xs rounded-full border transition-colors font-mono-data ${
                    !showCustomExtra && extraPayment === amt ? "bg-black text-white border-black" : "border-gray-200 hover:bg-gray-50 text-gray-600"
                  }`}>
                  {amt === 0 ? "Min only" : `+£${amt}`}
                </button>
              ))}
              {showCustomExtra ? (
                <div className="flex items-center gap-1">
                  <span className="text-xs text-gray-500">£</span>
                  <input data-testid="strategy-custom-input" type="number" min="0" step="1" value={customExtraVal}
                    onChange={(e) => setCustomExtraVal(e.target.value)}
                    onBlur={() => { const v = parseInt(customExtraVal); if (!isNaN(v) && v >= 0) setExtraPayment(v); }}
                    onKeyDown={(e) => { if (e.key === "Enter") { const v = parseInt(customExtraVal); if (!isNaN(v) && v >= 0) setExtraPayment(v); } }}
                    className="w-16 h-7 text-xs font-mono-data border border-gray-300 rounded-full px-2 text-center focus:border-black focus:outline-none"
                    autoFocus />
                </div>
              ) : (
                <button data-testid="strategy-custom-btn"
                  onClick={() => { setShowCustomExtra(true); setCustomExtraVal(String(![0,50,100,200,500].includes(extraPayment) ? extraPayment : "")); }}
                  className={`px-3 py-1 text-xs rounded-full border transition-colors font-mono-data ${
                    ![0,50,100,200,500].includes(extraPayment) ? "bg-black text-white border-black" : "border-gray-200 hover:bg-gray-50 text-gray-600"
                  }`}>
                  {![0,50,100,200,500].includes(extraPayment) ? `+£${extraPayment}` : "Custom"}
                </button>
              )}
            </div>
            {extraPayment > 0 && (
              <div className="mt-3 pt-3 border-t border-gray-200 grid grid-cols-2 gap-4 text-sm">
                <div>
                  <span className="text-gray-500">Snowball saves:</span>
                  <span className="font-mono-data font-semibold text-green-700 ml-2">{fmt(baseSnowball.totalInterest - snowball.totalInterest)}</span>
                  <span className="text-gray-400 ml-1">({baseSnowball.payoffMonth - snowball.payoffMonth} months faster)</span>
                </div>
                <div>
                  <span className="text-gray-500">Avalanche saves:</span>
                  <span className="font-mono-data font-semibold text-green-700 ml-2">{fmt(baseAvalanche.totalInterest - avalanche.totalInterest)}</span>
                  <span className="text-gray-400 ml-1">({baseAvalanche.payoffMonth - avalanche.payoffMonth} months faster)</span>
                </div>
              </div>
            )}
          </div>
          {/* Comparison Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className={`border rounded-sm p-5 ${winner === "snowball" ? "border-green-300 bg-green-50/30" : "border-gray-200"}`}>
              <div className="flex items-center justify-between mb-3">
                <h3 className="font-heading text-lg font-semibold tracking-tight">Snowball</h3>
                {winner === "snowball" && <Badge className="border border-green-400 text-green-700 bg-green-100 text-xs px-2 py-0.5 rounded-sm winner-shimmer" variant="outline">WINNER</Badge>}
              </div>
              <p className="text-xs text-gray-500 mb-4">Pay smallest balance first for quick wins.</p>
              <div className="space-y-2">
                <div className="flex justify-between text-sm"><span className="text-gray-600">Total Interest Paid</span><span className="font-mono-data font-semibold text-red-700">{fmt(snowball.totalInterest)}</span></div>
                <div className="flex justify-between text-sm"><span className="text-gray-600">Months to Payoff</span><span className="font-mono-data font-semibold">{snowball.payoffMonth}</span></div>
                <div className="mt-3 pt-3 border-t border-gray-200">
                  <p className="text-xs uppercase tracking-wider text-gray-500 font-semibold mb-1">Priority Order</p>
                  <div className="flex flex-wrap gap-1">
                    {snowball.order.map((name, i) => {
                      const d = debts.find(x => x.name === name);
                      const cliff = d?.promo_until;
                      return (
                        <Badge key={name} className="badge-pill text-[10px] px-2 py-0.5 font-mono-data border border-gray-300 bg-gray-50 text-gray-600" variant="outline">
                          {i+1}. {name}
                          {cliff && <span className="ml-1 text-amber-600">({cliff})</span>}
                        </Badge>
                      );
                    })}
                  </div>
                </div>
              </div>
            </div>

            <div className={`border rounded-sm p-5 ${!isNeutral && winner === "avalanche" ? "border-green-300 bg-green-50/30" : "border-gray-200"}`}>
              <div className="flex items-center justify-between mb-3">
                <h3 className="font-heading text-lg font-semibold tracking-tight">Avalanche</h3>
                {isNeutral
                  ? <Badge className="badge-pill border border-gray-300 text-gray-500 bg-gray-50 text-xs px-2 py-0.5" variant="outline">NEUTRAL</Badge>
                  : winner === "avalanche" && <Badge className="badge-pill border border-green-400 text-green-700 bg-green-100 text-xs px-2 py-0.5 winner-shimmer" variant="outline">WINNER</Badge>
                }
              </div>
              <p className="text-xs text-gray-500 mb-4">Pay highest APR first to minimise interest.</p>
              <div className="space-y-2">
                <div className="flex justify-between text-sm"><span className="text-gray-600">Total Interest Paid</span><span className="font-mono-data font-semibold text-red-700">{fmt(avalanche.totalInterest)}</span></div>
                <div className="flex justify-between text-sm"><span className="text-gray-600">Months to Payoff</span><span className="font-mono-data font-semibold">{avalanche.payoffMonth}</span></div>
                <div className="mt-3 pt-3 border-t border-gray-200">
                  <p className="text-xs uppercase tracking-wider text-gray-500 font-semibold mb-1">Priority Order</p>
                  <div className="flex flex-wrap gap-1">
                    {avalanche.order.map((name, i) => {
                      const d = debts.find(x => x.name === name);
                      const cliff = d?.promo_until;
                      return (
                        <Badge key={name} className="badge-pill text-[10px] px-2 py-0.5 font-mono-data border border-gray-300 bg-gray-50 text-gray-600" variant="outline">
                          {i+1}. {name}
                          {cliff && <span className="ml-1 text-amber-600">({cliff})</span>}
                        </Badge>
                      );
                    })}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Savings callout */}
          {isNeutral ? (
            <div className="border border-gray-200 rounded-sm p-4 bg-gray-50 text-center">
              <p className="text-sm text-gray-600">
                Strategy only matters with extra payments. Use the slider above to add extra monthly payments and see which strategy saves you more.
              </p>
            </div>
          ) : savings > 0 && (
            <div className="border border-green-300 rounded-sm p-4 bg-green-50/30 text-center">
              <p className="text-sm text-green-800">
                <span className="font-semibold">{winner === "avalanche" ? "Avalanche" : "Snowball"}</span> saves you{" "}
                <span className="font-mono-data font-bold text-green-700">{fmt(savings)}</span> in interest
                {Math.abs(avalanche.payoffMonth - snowball.payoffMonth) > 0 && (
                  <> and pays off <span className="font-mono-data font-bold">{Math.abs(avalanche.payoffMonth - snowball.payoffMonth)}</span> months sooner</>
                )}
              </p>
            </div>
          )}

          {/* Trajectory Charts */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div className="border border-gray-200 rounded-sm">
              <div className="px-4 py-3 border-b border-gray-200 bg-gray-50">
                <h3 className="font-heading text-sm font-semibold uppercase tracking-wider text-gray-600">Snowball Trajectory</h3>
              </div>
              <div className="p-4" style={{ height: 320 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={snowball.months} margin={{ left: 10, right: 10, bottom: 5 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke={gridColor} vertical={false} />
                    <XAxis dataKey="month" tick={{ fontSize: 10, fill: textColor }} interval={5} axisLine={false} tickLine={false} />
                    <YAxis tickFormatter={(v) => `£${(v/1000).toFixed(0)}k`} tick={{ fontSize: 10, fill: textColor }} axisLine={false} tickLine={false} />
                    <Tooltip formatter={(v) => fmt(v)} labelFormatter={(v) => `Month ${v}`} />
                    <Legend layout="vertical" align="right" verticalAlign="middle" wrapperStyle={{ fontSize: 9, paddingLeft: 8, pointerEvents: "none", userSelect: "none" }} iconType="circle" />
                    <Line type="monotone" dataKey="total" stroke={DEBT_COLORS[0]} strokeWidth={2.5} dot={false} name="Total" />
                    {snowball.order.slice(0, 4).map((name, i) => (
                      <Line key={name} type="monotone" dataKey={name} stroke={DEBT_COLORS[(i + 1) % DEBT_COLORS.length]} strokeWidth={1} dot={false} strokeDasharray={i > 0 ? "4 2" : undefined} />
                    ))}
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </div>

            <div className="border border-gray-200 rounded-sm">
              <div className="px-4 py-3 border-b border-gray-200 bg-gray-50">
                <h3 className="font-heading text-sm font-semibold uppercase tracking-wider text-gray-600">Avalanche Trajectory</h3>
              </div>
              <div className="p-4" style={{ height: 320 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={avalanche.months} margin={{ left: 10, right: 10, bottom: 5 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke={gridColor} vertical={false} />
                    <XAxis dataKey="month" tick={{ fontSize: 10, fill: textColor }} interval={5} axisLine={false} tickLine={false} />
                    <YAxis tickFormatter={(v) => `£${(v/1000).toFixed(0)}k`} tick={{ fontSize: 10, fill: textColor }} axisLine={false} tickLine={false} />
                    <Tooltip formatter={(v) => fmt(v)} labelFormatter={(v) => `Month ${v}`} />
                    <Legend layout="vertical" align="right" verticalAlign="middle" wrapperStyle={{ fontSize: 9, paddingLeft: 8, pointerEvents: "none", userSelect: "none" }} iconType="circle" />
                    <Line type="monotone" dataKey="total" stroke={DEBT_COLORS[0]} strokeWidth={2.5} dot={false} name="Total" />
                    {avalanche.order.slice(0, 4).map((name, i) => (
                      <Line key={name} type="monotone" dataKey={name} stroke={DEBT_COLORS[(i + 1) % DEBT_COLORS.length]} strokeWidth={1} dot={false} strokeDasharray={i > 0 ? "4 2" : undefined} />
                    ))}
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
